export type ColumnFilterMode = 'include' | 'exclude';

export interface ColumnFilter {
    mode: ColumnFilterMode;
    value: string;
}

/** Includes within a column use OR; columns use AND; any matching exclude rejects the row. */
export type ColumnFilters<ColumnId extends string> = Partial<Record<ColumnId, ColumnFilter[]>>;

export const addColumnFilter = <ColumnId extends string>(columnId: ColumnId, filter: ColumnFilter) =>
    ({ type: 'add', columnId, filter }) as const;
export const removeColumnFilter = <ColumnId extends string>(columnId: ColumnId, filter: ColumnFilter) =>
    ({ type: 'remove', columnId, filter }) as const;
export const clearColumnFilter = <ColumnId extends string>(columnId: ColumnId) =>
    ({ type: 'clear', columnId }) as const;
export const clearAllColumnFilters = () => ({ type: 'clearAll' }) as const;
/** Replaces every term of one column (an edit of the column's filter rather than an append). */
export const replaceColumnFilters = <ColumnId extends string>(columnId: ColumnId, filters: readonly ColumnFilter[]) =>
    ({ type: 'replace', columnId, filters }) as const;

type ColumnFilterAction<ColumnId extends string> = ReturnType<
    | typeof addColumnFilter<ColumnId>
    | typeof removeColumnFilter<ColumnId>
    | typeof clearColumnFilter<ColumnId>
    | typeof clearAllColumnFilters
    | typeof replaceColumnFilters<ColumnId>
>;

export function sameColumnFilter(a: ColumnFilter, b: ColumnFilter): boolean {
    return a.mode === b.mode && a.value.trim().toLowerCase() === b.value.trim().toLowerCase();
}

export function columnFilterReducer<ColumnId extends string>(
    state: ColumnFilters<ColumnId>,
    action: ColumnFilterAction<ColumnId>,
): ColumnFilters<ColumnId> {
    if (action.type === 'clearAll') return Object.keys(state).length ? {} : state;
    const terms = state[action.columnId] ?? [];
    switch (action.type) {
        case 'add': {
            const value = action.filter.value.trim();
            if (!value || terms.some((term) => sameColumnFilter(term, action.filter))) return state;
            return { ...state, [action.columnId]: [...terms, { ...action.filter, value }] };
        }
        case 'remove': {
            const remaining = terms.filter((term) => !sameColumnFilter(term, action.filter));
            if (remaining.length === terms.length) return state;
            if (remaining.length) return { ...state, [action.columnId]: remaining };
            return columnFilterReducer(state, clearColumnFilter(action.columnId));
        }
        case 'clear': {
            if (!(action.columnId in state)) return state;
            const next = { ...state };
            delete next[action.columnId];
            return next;
        }
        case 'replace': {
            const next = action.filters
                .map((term) => ({ ...term, value: term.value.trim() }))
                .filter(
                    (term, index, all) =>
                        term.value && all.findIndex((other) => sameColumnFilter(other, term)) === index,
                );
            if (!next.length) return columnFilterReducer(state, clearColumnFilter(action.columnId));
            return { ...state, [action.columnId]: next };
        }
    }
}

export interface ActiveColumnFilter<ColumnId extends string> extends ColumnFilter {
    key: string;
    columnId: ColumnId;
    label: string;
}

/** Flattens the non-blank terms in `columnOrder`, labelled for display as removable chips. */
export function getActiveColumnFilters<ColumnId extends string>(
    filters: ColumnFilters<ColumnId>,
    columnOrder: readonly ColumnId[],
    columnLabels: Readonly<Record<ColumnId, string>>,
    formatValue: (columnId: ColumnId, value: string) => string = (_, value) => value,
): ActiveColumnFilter<ColumnId>[] {
    return columnOrder.flatMap((columnId) =>
        (filters[columnId] ?? []).flatMap(({ mode, value }) => {
            const trimmed = value.trim();
            return trimmed
                ? [
                      {
                          columnId,
                          mode,
                          value,
                          key: JSON.stringify([columnId, mode, value]),
                          label: `${columnLabels[columnId]} ${mode === 'include' ? 'includes' : 'excludes'}: ${formatValue(columnId, trimmed)}`,
                      },
                  ]
                : [];
        }),
    );
}
